// Shared open-redirect guard for the ?next= deep-link param, plus the
// invite-context parser built on top of it.
//
// History: /login carried a local safeNextPath() since the middleware
// started appending ?next= to its redirects. INV-2 threads the same param
// through /signup and /verify-email so a seat-invited user keeps their
// invite destination across the whole sign-up chain, which makes this the
// third-plus surface and earns the hoist (same rule the formatRetryAfter
// comment in signup/page.tsx documents).
//
// Threat model: after auth we router.push(next), so an attacker could
// craft /login?next=https://evil.com (or the protocol-relative //evil.com,
// or a backslash variant) to bounce a freshly-authenticated user off-site
// for phishing. Only same-origin ROOT-relative paths are honoured.

/**
 * Return the sanitised same-origin path for a raw ?next= value, or null
 * when the value is missing, not root-relative, or escapes the origin.
 */
export function sanitizeNextPath(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/')) return null
  try {
    // Resolve against a sentinel origin; WHATWG URL normalises `//host`
    // and backslash tricks, so a changed origin means the path escaped.
    const u = new URL(raw, 'https://ps.invalid')
    if (u.origin !== 'https://ps.invalid') return null
    return u.pathname + u.search + u.hash
  } catch {
    return null
  }
}

/**
 * Sanitise a raw ?next= value, falling back to /dashboard (the historic
 * /login behaviour) when the value is unusable.
 */
export function safeNextPath(raw: string | null | undefined): string {
  return sanitizeNextPath(raw) ?? '/dashboard'
}

/**
 * Extract the team-invite token when a (sanitised) next path points at the
 * invite-acceptance page. Returns null for any other destination. This is
 * how /signup and /verify-email detect "this user is a seat invitee" and
 * skip the payment steps: invitees never see /select-plan or /checkout
 * because the payer already covers their seat.
 */
export function inviteTokenFromNext(next: string | null | undefined): string | null {
  const safe = sanitizeNextPath(next)
  if (!safe) return null
  try {
    const u = new URL(safe, 'https://ps.invalid')
    if (u.pathname !== '/team/accept-invite') return null
    const token = u.searchParams.get('token')
    return token && token.length > 0 ? token : null
  } catch {
    return null
  }
}
